/**
 * Minimalerei Process Steps — Phase 3.E.1 (revised)
 *
 * <mp-process-steps> Custom Element. Mobile-only carousel UX:
 *   - Dot-indicators built from the step count, click-to-jump
 *   - Active-dot tracking by scroll position (debounced via rAF)
 *   - One-shot "bounce" on first viewport entry that nudges the
 *     carousel right then back, hinting the swipe gesture
 *
 * Desktop layout uses a static grid; the JS no-ops there because the
 * dots/bounce only make sense on the carousel scroll-container.
 *
 * Wrapped in an IIFE with a customElements.get guard so re-injection
 * (Dawn re-renders the section on variant change, re-firing the
 * <script> tag in the block markup) doesn't throw "Identifier already
 * declared" — the class declaration stays scoped inside the IIFE.
 */

(function () {
  if (customElements.get('mp-process-steps')) return;

  class MpProcessSteps extends HTMLElement {
  constructor() {
    super();
    this.bouncePlayed = false;
  }

  connectedCallback() {
    this.grid = this.querySelector('[data-grid]');
    this.dotsContainer = this.querySelector('[data-dots]');
    this.steps = Array.from(this.querySelectorAll('.mp-process__step'));
    if (!this.grid || !this.dotsContainer || this.steps.length === 0) return;

    this.buildDots();
    this.bindScrollTracking();
    this.observeForBounce();
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
  }

  buildDots() {
    this.dots = this.steps.map((_, i) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'mp-process__dot';
      dot.setAttribute('aria-label', `Schritt ${i + 1}`);
      dot.dataset.dotIndex = i;
      if (i === 0) dot.classList.add('is-active');
      dot.addEventListener('click', () => this.scrollToStep(i));
      this.dotsContainer.appendChild(dot);
      return dot;
    });
  }

  scrollToStep(index) {
    const step = this.steps[index];
    if (!step) return;
    this.grid.scrollTo({ left: step.offsetLeft - this.grid.offsetLeft, behavior: 'smooth' });
  }

  bindScrollTracking() {
    let raf = null;
    this.grid.addEventListener('scroll', () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        this.updateActiveDot();
        raf = null;
      });
    });
  }

  updateActiveDot() {
    const scrollCenter = this.grid.scrollLeft + this.grid.clientWidth / 2;
    let activeIndex = 0;
    let minDist = Infinity;
    this.steps.forEach((step, i) => {
      const stepCenter = step.offsetLeft + step.offsetWidth / 2 - this.grid.offsetLeft;
      const dist = Math.abs(stepCenter - scrollCenter);
      if (dist < minDist) {
        minDist = dist;
        activeIndex = i;
      }
    });
    this.dots.forEach((dot, i) => dot.classList.toggle('is-active', i === activeIndex));
  }

  /**
   * Trigger a one-shot bounce when the section first enters the
   * viewport. Skipped on devices with reduced-motion preference and
   * on desktop (no scroll-overflow there). Uses native scrollBy with
   * smooth behaviour so it integrates with the snap-points naturally.
   */
  observeForBounce() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting || this.bouncePlayed) return;
          // Only bounce when the carousel actually has overflow
          // (i.e. mobile layout where scrollWidth > clientWidth).
          if (this.grid.scrollWidth <= this.grid.clientWidth) return;
          this.bouncePlayed = true;
          this.playBounce();
        });
      },
      { threshold: 0.4 },
    );
    this.observer.observe(this);
  }

  playBounce() {
    // Mandatory scroll-snap forces a full snap to the next card on
    // any programmatic scroll, so we briefly disable snapping for
    // the bounce to land at a partial peek (~30% of card 2 visible),
    // then re-enable snap after the return-scroll has settled.
    const distance = 80;
    const originalSnap = this.grid.style.scrollSnapType;
    this.grid.style.scrollSnapType = 'none';

    setTimeout(() => {
      this.grid.scrollBy({ left: distance, behavior: 'smooth' });
    }, 250);

    setTimeout(() => {
      this.grid.scrollBy({ left: -distance, behavior: 'smooth' });
    }, 900);

    setTimeout(() => {
      this.grid.style.scrollSnapType = originalSnap;
    }, 1600);
  }
}

  customElements.define('mp-process-steps', MpProcessSteps);
})();
